import React, { useEffect, useState } from "react";

import { addDays, addYears } from "date-fns";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import { InsuranceFormFields, InsuranceModalStateProviderProps } from "./types";
import { useFeature } from "flagged";
import InsuranceModal from "./InsuranceModal";
import useMutationUpdateInsurance from "./hooks/useMutationUpdateInsurance";
import useMutationUpdateInsuranceHistory from "./hooks/useMutationUpdateInsuranceHistory";
import validationSchema from "./validationSchema";

const InsuranceModalStateProvider: React.FC<InsuranceModalStateProviderProps> = ({
  vehicle,
  onComplete = () => {},
  onCancel = () => {},
  open = false,
  instructorId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const isFleetDatabaseSchemaMigrationEnabled = useFeature(
    "fleetDatabaseSchemaMigrationEnabled",
  );

  const {
    handleSubmit: submitWrapper,
    watch,
    errors,
    register,
    setValue,
    formState: { isValid, dirty },
    triggerValidation,
  } = useForm<InsuranceFormFields>({
    defaultValues: {
      insuranceStartDate: vehicle?.insurance?.endDate
        ? addDays(new Date(vehicle?.insurance?.endDate || undefined), 1)
        : undefined,
      insuranceEndDate: undefined,
      certificateNumber: undefined,
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    validationSchema,
  });

  useEffect(() => {
    register({ name: "insuranceStartDate" });
    register({ name: "insuranceEndDate" });
    register({ name: "certificateNumber" });
  }, []);
  const values = watch();

  const handleCertificateNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValue("certificateNumber", e.target.value);
    triggerValidation("certificateNumber");
  };

  const handleInsuranceStartDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValue("insuranceStartDate", new Date(e.target.value));
    setValue("insuranceEndDate", addYears(new Date(e.target.value), 1));
    triggerValidation("insuranceStartDate");
    triggerValidation("insuranceEndDate");
  };

  const handleInsuranceEndDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValue("insuranceEndDate", new Date(e.target.value));
    triggerValidation("insuranceEndDate");
  };

  const onMutationComplete = () => {
    onComplete({
      id: vehicle?.insurance?.id || "",
      certificateNumber: values.certificateNumber,
      startDate: values.insuranceStartDate,
      endDate: values.insuranceEndDate,
    });
    setSubmitting(false);
  };

  const [
    updateInsuranceHistory,
    { loading: loadingInsuranceHistory },
  ] = useMutationUpdateInsuranceHistory({
    onCompleted: onMutationComplete,
    onError: error => {
      enqueueSnackbar(`Error creating vehicle: ${error}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    },
    refetchQueries: ["InstructorDetails"],
  });

  const [updateInsurance, { loading }] = useMutationUpdateInsurance({
    onCompleted: onMutationComplete,
    onError: error => {
      enqueueSnackbar(`Error creating vehicle: ${error}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    },
    refetchQueries: ["InstructorDetails"],
  });

  const handleSubmit = submitWrapper(values => {
    setSubmitting(true);

    if (isFleetDatabaseSchemaMigrationEnabled) {
      updateInsuranceHistory({
        variables: {
          input: {
            id: vehicle?.insurance?.id,
            instructorId: instructorId,
            vehicleId: vehicle?.id,

            certificateNumber: values.certificateNumber,
            startDate: values.insuranceStartDate,
            endDate: values.insuranceEndDate,
          },
        },
      });
      return;
    }

    updateInsurance({
      variables: {
        id: vehicle?.id,
        instructorId,
        ...values,
      },
    });
  });

  const title = vehicle
    ? `Add insurance for ${vehicle.registration}`
    : `Add vehicle`;

  return (
    <InsuranceModal
      open={open}
      title={title}
      errors={errors}
      loading={loading || loadingInsuranceHistory}
      disabled={
        !isValid || !dirty || loading || submitting || loadingInsuranceHistory
      }
      onCancelClick={onCancel}
      onSubmit={handleSubmit}
      onInsuranceStartDateChange={handleInsuranceStartDateChange}
      onInsuranceEndDateChange={handleInsuranceEndDateChange}
      onCertificateNumberChange={handleCertificateNumberChange}
      values={values}
    />
  );
};

export default InsuranceModalStateProvider;
